import { useReactiveVar } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { MeetingNode } from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import Feedbacks from "@apps/dashboard-new/components/feedbacks";
import Recognitions from "@apps/dashboard-new/components/recognitions";
import useUiPreferenceCache, {
  UiPreferenceCache,
} from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Layout from "@components/layout/layout";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";

import AssessmentAskAI from "./assessment-ask-ai";
import { AssessmentSidebarTab } from "./assessment/assessment-constants";
import AssessmentGoalSidebar from "./assessment/assessment-goal-sidebar";
import RelatedRequirements from "./assessment/related-requirements";
import AssessmentRolesAndReviewsSidebar from "./assessment/role-and-reviews-sidebar";
import AssessmentContextSidebar from "./context-sidebar/context-sidebar";
import { assessmentContextSidebarVar } from "./context-sidebar/context-sidebar-context";

const AssessmentLayoutSidebar = ({
  target,
  periodStartDate,
  periodEndDate,
  relatedMeetings,
  isManager,
  sidebarExpandedUiPreferenceKey,
  assessmentId,
}: {
  target: BasicUser;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  relatedMeetings: Pick<
    MeetingNode,
    | "id"
    | "title"
    | "meetingGroupId"
    | "startDatetime"
    | "endDatetime"
    | "draft"
  >[];
  isManager: boolean;
  sidebarExpandedUiPreferenceKey: keyof UiPreferenceCache;
  assessmentId?: number;
}) => {
  const organization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const [chatSessionId, setChatSessionId] = useState<null | number>(null);
  const [startedChatSession, setStartedChatSession] = useState(true);
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const hasNoContentInArtifactTab =
    (!organization.featureFlags.meetings ||
      (!organization.featureFlags.meetings && relatedMeetings.length === 0)) &&
    !organization.featureFlags.goals &&
    !organization.featureFlags.recognitions &&
    !organization.featureFlags.feedbacks;

  const tabs = useMemo(() => {
    return compact([
      !hasNoContentInArtifactTab && {
        title: AssessmentSidebarTab.artifacts,
        value: AssessmentSidebarTab.artifacts,
      },
      organization.featureFlags.workHistory && {
        title: AssessmentSidebarTab.workHistory,
        value: AssessmentSidebarTab.workHistory,
      },
      organization.featureFlags.askAi && {
        title: AssessmentSidebarTab.askAI,
        value: AssessmentSidebarTab.askAI,
      },
      {
        title: AssessmentSidebarTab.rolesAndReviews,
        value: AssessmentSidebarTab.rolesAndReviews,
      },
      organization.featureFlags.reviewContextGeneration &&
        waffle.flag_is_active("review-context-sidebar") && {
          title: AssessmentSidebarTab.context,
          value: AssessmentSidebarTab.context,
        },
    ]);
  }, [
    organization.featureFlags.workHistory,
    organization.featureFlags.askAi,
    hasNoContentInArtifactTab,
    organization.featureFlags.reviewContextGeneration,
  ]);

  // Perist last selected tab
  // or, if items tab is empty, show roles and reviews tab
  const defaultTabValue =
    uiPreferenceCache.assessmentSidebarSelectedTab ||
    (hasNoContentInArtifactTab && target
      ? AssessmentSidebarTab.rolesAndReviews
      : AssessmentSidebarTab.artifacts);
  const selectedActiveTab =
    tabs.find((tab) => tab.value === defaultTabValue) || tabs[0];

  const [activeTab, setActiveTab] = useState(selectedActiveTab.value);

  const handleChangeTab = (
    tab: ToggleButtonGroupType<AssessmentSidebarTab>,
  ) => {
    if (!tab.value) {
      return;
    }
    setActiveTab(tab.value);
    saveUiPreference({
      assessmentSidebarSelectedTab: tab.value,
    });
  };

  const assessmentContextSidebar = useReactiveVar(assessmentContextSidebarVar);
  useEffect(() => {
    if (
      assessmentContextSidebar &&
      organization.featureFlags.reviewContextGeneration
    ) {
      const hasContextTab = tabs.some(
        (tab) => tab.value === AssessmentSidebarTab.context,
      );
      const hasAskAITab = tabs.some(
        (tab) => tab.value === AssessmentSidebarTab.askAI,
      );
      if (
        hasContextTab &&
        organization.featureFlags.reviewContextGeneration &&
        waffle.flag_is_active("review-context-sidebar")
      ) {
        setActiveTab(AssessmentSidebarTab.context);
        saveUiPreference({
          assessmentSidebarSelectedTab: AssessmentSidebarTab.context,
        });
      } else if (
        waffle.flag_is_active("generated-context-in-ask-ai") &&
        hasAskAITab
      ) {
        setActiveTab(AssessmentSidebarTab.askAI);
        saveUiPreference({
          assessmentSidebarSelectedTab: AssessmentSidebarTab.askAI,
        });
      }
    }
  }, [assessmentContextSidebar, tabs]);

  return (
    <Layout.Sidebar<AssessmentSidebarTab>
      sidebarExpandedUiPreferenceKey={sidebarExpandedUiPreferenceKey}
      className="bg-white"
      onChangeTab={handleChangeTab}
      tabs={tabs.map((tab) => ({
        ...tab,
        active: tab.value === activeTab,
      }))}
      fullHeight={activeTab === AssessmentSidebarTab.askAI}
    >
      {activeTab === AssessmentSidebarTab.askAI ? (
        <AssessmentAskAI
          startedChatSession={startedChatSession}
          chatSessionId={chatSessionId}
          onChangeChatSessionId={setChatSessionId}
          assessmentId={assessmentId}
          onStartChatSession={() => {
            setStartedChatSession(true);
          }}
        />
      ) : activeTab === AssessmentSidebarTab.workHistory ? (
        <WorkHistorySidebar
          userId={target.id}
          startDate={
            periodStartDate || moment().subtract(7, "days").format("YYYY-MM-DD")
          }
          endDate={periodEndDate || moment().format("YYYY-MM-DD")}
          forceDateFilter={true}
          hasViewingPermission={isManager || currentUser.id === target.id}
        />
      ) : activeTab === AssessmentSidebarTab.artifacts ? (
        <>
          {hasNoContentInArtifactTab && (
            <Layout.SidebarSection>
              <Layout.SidebarSubSectionEmpty>
                No related data.
              </Layout.SidebarSubSectionEmpty>
            </Layout.SidebarSection>
          )}

          {organization.featureFlags.meetings && (
            <RelatedRequirements relatedMeetings={relatedMeetings} />
          )}

          {organization.featureFlags.goals && target && (
            <AssessmentGoalSidebar
              selectedUser={target}
              goalDueBetweenDates={
                periodStartDate && periodEndDate
                  ? [periodStartDate, periodEndDate]
                  : undefined
              }
            />
          )}

          {organization.featureFlags.recognitions && target && (
            <Recognitions
              selectedUser={target}
              createdBetweenDates={
                periodStartDate && periodEndDate
                  ? [periodStartDate, periodEndDate]
                  : undefined
              }
            />
          )}
          {organization.featureFlags.feedbacks && target && (
            <Feedbacks
              selectedUser={target}
              createdBetweenDates={
                periodStartDate && periodEndDate
                  ? [periodStartDate, periodEndDate]
                  : undefined
              }
            />
          )}
        </>
      ) : activeTab === AssessmentSidebarTab.rolesAndReviews && target ? (
        <AssessmentRolesAndReviewsSidebar target={target} />
      ) : activeTab === AssessmentSidebarTab.context &&
        organization.featureFlags.reviewContextGeneration &&
        target ? (
        <AssessmentContextSidebar />
      ) : null}
    </Layout.Sidebar>
  );
};

export default AssessmentLayoutSidebar;
